import { ui } from "@patient-watch/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@patient-watch/ui/tabs";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { ClipboardCheck } from "lucide-react";
import { useState } from "react";
import { useValidatorsQuery } from "../api/selectValidators";
import { useInviteFormValidation, useInviteScheduleValidation } from "../api/useInviteValidation";
import {
    useRecordExternalFormValidation,
    useRecordExternalScheduleValidation,
} from "../api/useRecordExternalValidation";
import type { ValidationOutcomeT } from "./ValidationsList";

interface Props {
    entityType: "form" | "schedule";
    entityId: string;
}

export interface ValidationRecordedResultT {
    status: ValidationOutcomeT;
}

interface DoneProps {
    onDone: (result?: ValidationRecordedResultT) => void;
}

const inviteSchema = z.object({ validated_by: z.string().uuid("Please select a validator") });
type InviteFormSchemaT = z.infer<typeof inviteSchema>;

const externalSchema = z.object({
    is_valid: z.enum(["true", "false"]),
    on_behalf_of_name: z.string().min(1, "Name is required"),
    on_behalf_of_organisation: z.string().optional(),
    reason: z.string().optional(),
});
type ExternalFormSchemaT = z.infer<typeof externalSchema>;

function InviteValidatorTab({ entityType, entityId, onDone }: Props & DoneProps) {
    const { data: validators = [] } = useValidatorsQuery();
    const inviteForm = useInviteFormValidation();
    const inviteSchedule = useInviteScheduleValidation();
    const isPending = inviteForm.isPending || inviteSchedule.isPending;

    const form = useForm<InviteFormSchemaT>({
        resolver: zodResolver(inviteSchema),
        defaultValues: { validated_by: undefined },
    });

    const onSubmit = (data: InviteFormSchemaT) => {
        const mutation = entityType === "form" ? inviteForm : inviteSchedule;
        const payload =
            entityType === "form"
                ? { form_id: entityId, validated_by: data.validated_by }
                : { schedule_id: entityId, validated_by: data.validated_by };
        mutation.mutate(payload as never, { onSuccess: () => onDone({ status: "pending" }) });
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <fc.Select
                    name="validated_by"
                    label="Validator"
                    options={validators.map((v) => ({
                        value: v.user_id as string,
                        label: `${v.first_name ?? ""} ${v.last_name ?? ""}`.trim() || v.email || "Unnamed validator",
                    }))}
                />
                {validators.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No users have the validator role yet.</p>
                ) : null}
                <ui.DialogFooter>
                    <fc.CancelButton variant="outline" size="default" onClick={onDone} className="w-auto" />
                    <fc.AddButton
                        buttonType="submit"
                        variant="default"
                        label="Send invite"
                        isLoading={isPending}
                        className="w-auto"
                        disabled={validators.length === 0}
                    />
                </ui.DialogFooter>
            </form>
        </FormProvider>
    );
}

function ExternalSignOffTab({ entityType, entityId, onDone }: Props & DoneProps) {
    const recordForm = useRecordExternalFormValidation();
    const recordSchedule = useRecordExternalScheduleValidation();
    const isPending = recordForm.isPending || recordSchedule.isPending;

    const form = useForm<ExternalFormSchemaT>({
        resolver: zodResolver(externalSchema),
        defaultValues: { is_valid: "true", on_behalf_of_name: "", on_behalf_of_organisation: "", reason: "" },
    });
    const isValid = form.watch("is_valid");

    const onSubmit = (data: ExternalFormSchemaT) => {
        if (data.is_valid === "false" && !data.reason?.trim()) {
            form.setError("reason", { message: "A reason is required when rejecting" });
            return;
        }
        const mutation = entityType === "form" ? recordForm : recordSchedule;
        const base = {
            is_valid: data.is_valid === "true",
            reason: data.reason || undefined,
            on_behalf_of_name: data.on_behalf_of_name,
            on_behalf_of_organisation: data.on_behalf_of_organisation || undefined,
        };
        const payload = entityType === "form" ? { ...base, form_id: entityId } : { ...base, schedule_id: entityId };
        mutation.mutate(payload as never, {
            onSuccess: () => onDone({ status: data.is_valid === "true" ? "approved" : "rejected" }),
        });
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <fc.Select
                    name="is_valid"
                    label="Outcome"
                    options={[
                        { value: "true", label: "Approved" },
                        { value: "false", label: "Rejected" },
                    ]}
                />
                <div>
                    <ui.Label htmlFor="on_behalf_of_name">Signed off by</ui.Label>
                    <ui.Input
                        id="on_behalf_of_name"
                        {...form.register("on_behalf_of_name")}
                        placeholder="Dr. Jane Smith"
                    />
                </div>
                <div>
                    <ui.Label htmlFor="on_behalf_of_organisation">Organisation (optional)</ui.Label>
                    <ui.Input
                        id="on_behalf_of_organisation"
                        {...form.register("on_behalf_of_organisation")}
                        placeholder="MAPI Research Trust"
                    />
                </div>
                {isValid === "false" ? (
                    <div>
                        <ui.Label htmlFor="reason">Reason</ui.Label>
                        <ui.Textarea id="reason" {...form.register("reason")} rows={3} />
                    </div>
                ) : null}
                <ui.DialogFooter>
                    <fc.CancelButton variant="outline" size="default" onClick={onDone} className="w-auto" />
                    <fc.AddButton
                        buttonType="submit"
                        variant="default"
                        label="Record sign-off"
                        isLoading={isPending}
                        className="w-auto"
                    />
                </ui.DialogFooter>
            </form>
        </FormProvider>
    );
}

/** Inner tabs content, reused by the admin "New validation" dialog once an entity has been picked there. */
export function InviteToValidateTabs({ entityType, entityId, onDone }: Props & DoneProps) {
    return (
        <Tabs defaultValue="invite">
            <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="invite">Invite a validator</TabsTrigger>
                <TabsTrigger value="external">Record external sign-off</TabsTrigger>
            </TabsList>
            <TabsContent value="invite" className="pt-4">
                <InviteValidatorTab entityType={entityType} entityId={entityId} onDone={onDone} />
            </TabsContent>
            <TabsContent value="external" className="pt-4">
                <ExternalSignOffTab entityType={entityType} entityId={entityId} onDone={onDone} />
            </TabsContent>
        </Tabs>
    );
}

const InviteToValidateDialog = ({ entityType, entityId }: Props) => {
    const [open, setOpen] = useState(false);
    const onDone = () => setOpen(false);

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <ui.DialogTrigger asChild>
                <ui.Button
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto whitespace-nowrap"
                >
                    <ClipboardCheck className="w-4 h-4" />
                    Invite to validate
                </ui.Button>
            </ui.DialogTrigger>
            <ui.DialogContent>
                <ui.DialogHeader>
                    <ui.DialogTitle>Invite to validate</ui.DialogTitle>
                    <ui.DialogDescription>
                        Assign a validator to review this {entityType} in-app, or record a sign-off received outside the
                        system.
                    </ui.DialogDescription>
                </ui.DialogHeader>
                <InviteToValidateTabs entityType={entityType} entityId={entityId} onDone={onDone} />
            </ui.DialogContent>
        </ui.Dialog>
    );
};

export default InviteToValidateDialog;
